import { UserInfo, UserState, Subscription } from '@/types'
import { getUserInfo, getUserSubscriedInfo, googleLogin, appleLogin, qrCodeLogin } from '../../api/user'
import { $notification, $t, $loading } from '../../utils'
import { REQUEST_NOAUTH_CODE, REQUEST_SUCCESS_CODE } from '../../config/constant'
import { emailLogin, getGrade } from '@/api/user'
import { logEvent,setCustomerUserId } from '@/utils/utils.ts'
import { getUserToken, cacheUserToken, removeUserToken, removeLastActiveTime } from '@/utils/user.ts'
import { bindDevice } from '@/api/user'
import { startFirestoreListener, stopFirestoreListener } from '@/utils/firestoreSparks.ts'

import { getRouter } from '@/router'

// 默认状态
const getCachedUserInfo = () => {
    if (typeof localStorage === 'undefined') return null
    try {
        const cached = localStorage.getItem('SR_USER_INFO')
        return cached ? JSON.parse(cached) : null
    } catch {
        return null
    }
}

const getCachedGradeList = () => {
    if (typeof localStorage === 'undefined') return []
    try {
        const cached = localStorage.getItem('SR_GRADE_LIST')
        return cached ? JSON.parse(cached) : []
    } catch {
        return []
    }
}

const state: UserState = {
    userInfo: getCachedUserInfo(),
    token: getUserToken(),
    isLoggedIn: getUserToken() ? true : false,
    isLoading: false,
    gradeList: getCachedGradeList()
}

// Getters
const getters = {
    userInfo: (state: UserState) => state.userInfo,
    token: (state: UserState) => state.token,
    isLoggedIn: (state: UserState) => state.isLoggedIn,
    isLoading: (state: UserState) => state.isLoading,
    userId: (state: UserState) => state.userInfo?.userId || '',
    uuid: (state: UserState) => state.userInfo?.uuid || '',
    nickName: (state: UserState) => state.userInfo?.nickName || '',
    email: (state: UserState) => state.userInfo?.email || '',
    accountType: (state: UserState) => state.userInfo?.accountType || '',
    avatarUrl: (state: UserState) => state.userInfo?.avatarUrl || '',
    subscriptions: (state: UserState) => state.userInfo?.subscriptions || [],
    isAnonymous: (state: UserState) => state.userInfo?.accountType === 'ANONYMOUS',
    birthDate: (state: UserState) => state.userInfo?.birthDate || '',
    birthDateTimeSpan: (state: UserState) => state.userInfo?.birthDateTimeSpan || null,
    gradeLevelCode: (state: UserState) => state.userInfo?.gradeLevelCode || '',
    gradeLevelOther: (state: UserState) => state.userInfo?.gradeLevelOther || '',
    gradeList: (state: UserState) => state.gradeList || [],
    webSparks: (state: UserState) => state.userInfo?.sparks?.webSparks || 0
}

// Mutations
const mutations = {
    SET_USER_INFO(state: UserState, userInfo: UserInfo) {
        state.userInfo = userInfo
        state.isLoggedIn = true
        localStorage.setItem('SR_USER_INFO', JSON.stringify(userInfo))
    },
    CLEAR_USER_INFO(state: UserState) {
        state.userInfo = null
        state.isLoggedIn = false
        localStorage.removeItem('SR_USER_INFO')
    },
    SET_LOADING(state: UserState, loading: boolean) {
        state.isLoading = loading
    },
    UPDATE_USER_FIELD(state: UserState, { field, value }: { field: keyof UserInfo; value: any }) {
        if (state.userInfo) {
            ;(state.userInfo as any)[field] = value
        }
    },
    UPDATE_SUBSCRIPTIONS(state: UserState, subscriptions: Subscription[]) {
        if (state.userInfo) {
            state.userInfo.subscriptions = subscriptions
        }
    },
    SET_USER_TOKEN(state: UserState, token: string) {
        state.token = token
    },
    REMOVE_USER_TOKEN(state: UserState) {
        state.token = null
    },

    CACHE_GRADE_LIST(state: UserState, gradeList: any) {
        state.gradeList = gradeList
        localStorage.setItem('SR_GRADE_LIST', JSON.stringify(gradeList))
    },

    SET_SPARKS(state: UserState, payload: { webSparks: number; refreshed: boolean }) {
        if (!state.userInfo) return
        if (!state.userInfo.sparks) {
            state.userInfo.sparks = {}
        }
        state.userInfo.sparks.webSparks = payload.webSparks
        state.userInfo.sparks.refreshed = payload.refreshed

        // 同步 localStorage 缓存（保持与 SET_USER_INFO 一致的行为）
        try {
            const cached = localStorage.getItem('SR_USER_INFO')
            if (cached) {
                const parsed = JSON.parse(cached)
                if (!parsed.sparks) {
                    parsed.sparks = {}
                }
                parsed.sparks.webSparks = payload.webSparks
                parsed.sparks.refreshed = payload.refreshed
                localStorage.setItem('SR_USER_INFO', JSON.stringify(parsed))
            }
        } catch (e) {
            // ignore
        }
    }
}

// Actions
const actions = {
    // 缓存用户Token
    setUserToken({ commit }: any, token: string) {
        commit('SET_USER_TOKEN', token)
        cacheUserToken(token)
    },
    removeUserToken({ commit }: any) {
        commit('SET_USER_TOKEN', null)
        removeUserToken()
    },

    // 设置用户信息
    setUserInfo({ commit }: any, userInfo: UserInfo) {
        commit('SET_USER_INFO', userInfo)
    },

    // 清除用户信息
    clearUserInfo({ commit }: any) {
        stopFirestoreListener()
        commit('CLEAR_USER_INFO')
        commit('REMOVE_USER_TOKEN')
        removeUserToken()
        removeLastActiveTime()
    },

    // 更新用户信息字段
    updateUserField({ commit }: any, payload: { field: keyof UserInfo; value: any }) {
        commit('UPDATE_USER_FIELD', payload)
    },

    // 更新订阅信息
    updateSubscriptions({ commit }: any, subscriptions: Subscription[]) {
        commit('UPDATE_SUBSCRIPTIONS', subscriptions)
    },

    // 刷新用户信息（主要用于刷新sparks余额）
    async refreshUserInfo({ dispatch }: any) {
        const userInfo = await getUserInfo()
        const userSubscriedInfo = await getUserSubscriedInfo()

        userInfo.data.subscriptions = userSubscriedInfo.data

        if (userInfo.code === REQUEST_SUCCESS_CODE) {
            await dispatch('setUserInfo', userInfo.data)
        }
    },

    // 退出登录
    async logout({ dispatch }: any) {
        await dispatch('clearUserInfo')
    },

    // Google登录
    async googleLogin({ dispatch }: any, { googleUserInfo, accessToken }: any) {
        try {
            const res = await googleLogin({
                email: googleUserInfo.email,
                headimgurl: googleUserInfo.picture,
                nickname: googleUserInfo.name,
                openid: googleUserInfo.id,
                unionid: googleUserInfo.id,
                // openId: googleUserInfo.id,
                nickName: googleUserInfo.name,
                avatarUrl: googleUserInfo.picture
            })

            if (res.code === REQUEST_SUCCESS_CODE) {
                // 设置用户信息和token
                await bindDevice()
                await dispatch('setUserToken', res.data.token)
                await dispatch('initUserInfo')
                // initUserInfo 内已 setCustomerUserId，再打点以便 AF/GA 带上用户身份
                logEvent('web_login_success', {
                    method: 'google',
                    substatus: ''
                })
            } else {
                logEvent('web_login_fail', {
                    method: 'google',
                    error_code: 'auth_error',
                })
                throw new Error(JSON.stringify({ status: 'error', error: res.msg || $t('login.googleLoginError') }))
            }
        } catch (error) {
            logEvent('web_login_fail', {
                method: 'google',
                error_code: 'exception',
            })

            throw error
        }
    },

    // Apple登录
    async appleLogin({ dispatch }: any, { appleInfo }: any) {
        try {
            const res = await appleLogin({
                appleInfo
            })

            if (res.code === REQUEST_SUCCESS_CODE) {
                await bindDevice()
                await dispatch('setUserToken', res.data.token)
                await dispatch('initUserInfo')
                logEvent('web_login_success', {
                    method: 'apple',
                    substatus: ''
                })
            } else {
                logEvent('web_login_fail', {
                    method: 'apple',
                    error_code: 'auth_error'
                })
                throw new Error(JSON.stringify({ status: 'error', error: res.msg || $t('login.googleLoginError') }))
            }
        } catch (error) {
            logEvent('web_login_fail', {
                method: 'apple',
                error_code: 'exception',
            })

            throw error
        }
    },

    // 二维码登录
    async qrCodeLogin({ dispatch }: any, { qrcode }: any) {
        try {
            const res = await qrCodeLogin(qrcode)

            if (res.code === REQUEST_SUCCESS_CODE && res?.data?.token) {
                await bindDevice()
                await dispatch('setUserToken', res.data.token)
                await dispatch('initUserInfo')
                logEvent('web_login_success', {
                    method: 'qr_code',
                    substatus: ''
                })

                return res // 返回成功结果
            } else {
                // 返回失败结果，但不抛出异常（用于轮询检查）
                return res
            }
        } catch (error) {
            logEvent('web_login_fail', {
                method: 'qr_code',
                error_code: 'exception',
            })

            throw error
        }
    },

    async emailLogin({ dispatch }: any, { email, code }: any) {
        $loading.loading()
        return emailLogin({ email, code })
            .then(async res => {
                if (res.code == REQUEST_SUCCESS_CODE) {
                    await bindDevice()
                    await dispatch('setUserToken', res.data.token)
                    await dispatch('initUserInfo')
                    logEvent('web_login_success', {
                        method: 'email',
                        substatus: ''
                    })
                    $loading.hide()
                    return res
                } else {
                    logEvent('web_login_fail', {
                        method: 'email',
                        error_code: 'auth_error',
                    })

                    $loading.hide()
                    $notification.error(res.msg)
                    return $t('login.wrongVCode')
                }
            })
            .catch(() => {
                logEvent('web_login_fail', {
                    method: 'email',
                    error_code: 'exception'
                })

                $notification.error($t('login.emailCodeError'))
                $loading.hide()
                return $t('login.wrongVCode')
            })
    },

    async initUserInfo({ dispatch, commit, state }: any) {
        const token = state.token

        // 每次初始化前先清理旧监听，防止用户切换或重复初始化时 listener 残留
        stopFirestoreListener()

        try {
            if (token) {
                commit('SET_USER_TOKEN', token)

                const userInfo = await getUserInfo()
                const userSubscriedInfo = await getUserSubscriedInfo()

                userInfo.data.subscriptions = userSubscriedInfo.data

                if (userInfo.code === REQUEST_SUCCESS_CODE) {
                    await dispatch('setUserInfo', userInfo.data)
                    await dispatch('chat/initDatabase', null, { root: true })
                    setCustomerUserId(userInfo.data.userId)
                    startFirestoreListener(userInfo.data.userId)
                } else {
                    if (userInfo.code === REQUEST_NOAUTH_CODE) {
                        throw new Error(JSON.stringify({ code: REQUEST_NOAUTH_CODE, error: $t('common.noAuth') }))
                    }
                    console.error(`用户信息初始化失败-code:${userInfo.code}`, userInfo)
                }
            } else {
                throw new Error(JSON.stringify({ code: REQUEST_NOAUTH_CODE, error: $t('common.noAuth') }))
            }
        } catch (err: any) {
            // 异常时确保监听器已清理，避免残留
            stopFirestoreListener()

            try {
                const error = JSON.parse(err?.message)

                if (error.code === REQUEST_NOAUTH_CODE) {
                    await dispatch('logout')

                    if (getRouter().currentRoute.value.meta.requireAuth) {
                        await getRouter().replace({ name: 'home' })
                        return
                    }
                }
            } catch (e) {
                if (err?.code === REQUEST_NOAUTH_CODE) {
                    await dispatch('logout')

                    if (getRouter().currentRoute.value.meta.requireAuth) {
                        await getRouter().replace({ name: 'home' })
                        return
                    }
                }
            }
        }
    },

    cacheGradeList({ commit }: any) {
        return getGrade().then(res => {
            if (res.code == REQUEST_SUCCESS_CODE) {
                commit('CACHE_GRADE_LIST', res.data)
            }
        })
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
